import React, { useState, useEffect } from 'react';

const ContractDetail = ({ contractId }) => {
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchContract();
  }, [contractId]);

  const fetchContract = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/contracts/${contractId}`);
      if (!response.ok) {
        throw new Error('Contract not found');
      }
      const data = await response.json();
      setContract(data);
    } catch (error) {
      console.error('Error fetching contract:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return <div className="loading">Loading contract details...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  if (!contract) {
    return <div className="error">Contract not found</div>;
  }

  const licensor = contract.contract_parties?.find(cp => cp.role === 'licensor');
  const licensees = contract.contract_parties?.filter(cp => cp.role === 'licensee') || [];

  return (
    <div className="contract-detail-container">
      <div className="header">
        <div>
          <h1>{contract.contract_number}</h1>
          <span className={`badge badge-${contract.status}`}>
            {contract.status.replace('_', ' ')}
          </span>
        </div>
        <div className="actions">
          <a href={`/contracts/${contract.id}/edit`} className="btn btn-primary">Edit</a>
          <a href="/contracts" className="btn">Back to List</a>
        </div>
      </div>

      <div className="contract-info-card">
        <h2>Contract Information</h2>

        <div className="info-grid">
          <div className="info-item full-width">
            <span className="label">Title:</span>
            <span className="value-large">{contract.title}</span>
          </div>

          {contract.description && (
            <div className="info-item full-width">
              <span className="label">Description:</span>
              <span className="value">{contract.description}</span>
            </div>
          )}

          <div className="info-item">
            <span className="label">Contract Number:</span>
            <span className="value">{contract.contract_number}</span>
          </div>

          <div className="info-item">
            <span className="label">Status:</span>
            <span className={`badge badge-${contract.status}`}>
              {contract.status.replace('_', ' ')}
            </span>
          </div>

          <div className="info-item">
            <span className="label">Territory:</span>
            <span className="value">{contract.territory?.name}</span>
          </div>

          <div className="info-item">
            <span className="label">Start Date:</span>
            <span className="value">{formatDate(contract.start_date)}</span>
          </div>

          <div className="info-item">
            <span className="label">End Date:</span>
            <span className="value">{formatDate(contract.end_date)}</span>
          </div>

          <div className="info-item">
            <span className="label">Signed Date:</span>
            <span className="value">{formatDate(contract.signed_date)}</span>
          </div>
        </div>
      </div>

      <div className="parties-section">
        <h2>Contract Parties</h2>

        <div className="party-grid">
          {licensor && (
            <div className="party-role-card licensor">
              <h3>Licensor</h3>
              <div className="party-info">
                <h4>
                  <a href={`/parties/${licensor.party.id}`}>{licensor.party.name}</a>
                </h4>
                <div className="party-details">
                  <div className="detail-item">
                    <span className="label">Type:</span>
                    <span>{licensor.party.party_type.replace('_', ' ')}</span>
                  </div>
                  {licensor.party.contact_email && (
                    <div className="detail-item">
                      <span className="label">Email:</span>
                      <span>{licensor.party.contact_email}</span>
                    </div>
                  )}
                  {licensor.exclusive && (
                    <div className="exclusive-badge">Exclusive</div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="licensees-card">
            <h3>Licensees ({licensees.length})</h3>
            {licensees.map((licensee) => (
              <div key={licensee.id} className="party-info">
                <h4>
                  <a href={`/parties/${licensee.party.id}`}>{licensee.party.name}</a>
                </h4>
                <div className="party-details">
                  <div className="detail-item">
                    <span className="label">Type:</span>
                    <span>{licensee.party.party_type.replace('_', ' ')}</span>
                  </div>
                  {licensee.party.contact_email && (
                    <div className="detail-item">
                      <span className="label">Email:</span>
                      <span>{licensee.party.contact_email}</span>
                    </div>
                  )}
                  {licensee.exclusive && (
                    <div className="exclusive-badge">Exclusive</div>
                  )}
                  {licensee.rights_granted && (
                    <div className="detail-item full">
                      <span className="label">Rights Granted:</span>
                      <span>{licensee.rights_granted}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="licensed-ip-section">
        <h2>Licensed IP Objects ({contract.contract_ip_objects?.length || 0})</h2>

        {contract.contract_ip_objects && contract.contract_ip_objects.length > 0 ? (
          <div className="ip-objects-table">
            <table>
              <thead>
                <tr>
                  <th>IP Object</th>
                  <th>Rights Category</th>
                  <th>Field of Use</th>
                  <th>Sublicense</th>
                  <th>Effective Date</th>
                  <th>Termination Date</th>
                </tr>
              </thead>
              <tbody>
                {contract.contract_ip_objects.map((cip) => (
                  <tr key={cip.id}>
                    <td>
                      <a href={`/ip_objects/${cip.ip_object.id}`}>{cip.ip_object.name}</a>
                    </td>
                    <td>
                      <strong>{cip.rights_category.name}</strong>
                      {cip.rights_category.description && (
                        <div className="description">{cip.rights_category.description}</div>
                      )}
                    </td>
                    <td>{cip.field_of_use || '-'}</td>
                    <td>
                      <span className={`badge ${cip.sublicense_permitted ? 'badge-yes' : 'badge-no'}`}>
                        {cip.sublicense_permitted ? 'Permitted' : 'Not Permitted'}
                      </span>
                    </td>
                    <td>{formatDate(cip.effective_date)}</td>
                    <td>{formatDate(cip.termination_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            No IP objects licensed under this contract.
          </div>
        )}
      </div>

      <style jsx>{`
        .contract-detail-container {
          padding: 20px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .header {
          display: flex;
          justify-content: space-between;
          align-items: start;
          margin-bottom: 30px;
        }

        .header h1 {
          margin: 0 0 10px 0;
          font-size: 28px;
          color: #333;
        }

        .actions {
          display: flex;
          gap: 10px;
        }

        .contract-info-card,
        .parties-section,
        .licensed-ip-section {
          background: white;
          border: 1px solid #ddd;
          border-radius: 8px;
          padding: 25px;
          margin-bottom: 20px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        h2 {
          margin: 0 0 20px 0;
          font-size: 20px;
          color: #333;
          border-bottom: 2px solid #2563eb;
          padding-bottom: 10px;
        }

        .info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 20px;
        }

        .info-item {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }

        .info-item.full-width {
          grid-column: 1 / -1;
        }

        .info-item .label {
          font-weight: 600;
          color: #666;
          font-size: 14px;
        }

        .info-item .value {
          color: #333;
          font-size: 16px;
        }

        .info-item .value-large {
          color: #333;
          font-size: 18px;
          font-weight: 500;
        }

        .party-grid {
          display: grid;
          grid-template-columns: 1fr 2fr;
          gap: 20px;
        }

        .party-role-card, .licensees-card {
          border: 1px solid #eee;
          border-radius: 6px;
          padding: 20px;
          background: #f9fafb;
        }

        .party-role-card.licensor {
          border-color: #2563eb;
          background: #eff6ff;
        }

        .party-role-card h3, .licensees-card h3 {
          margin: 0 0 15px 0;
          font-size: 16px;
          color: #666;
          text-transform: uppercase;
          font-weight: 600;
        }

        .party-info {
          margin-bottom: 15px;
        }

        .party-info:last-child {
          margin-bottom: 0;
        }

        .party-info h4 {
          margin: 0 0 10px 0;
          font-size: 18px;
        }

        .party-info h4 a {
          color: #2563eb;
          text-decoration: none;
        }

        .party-info h4 a:hover {
          text-decoration: underline;
        }

        .party-details {
          display: grid;
          gap: 8px;
        }

        .detail-item {
          display: flex;
          gap: 8px;
          font-size: 14px;
        }

        .detail-item.full {
          flex-direction: column;
          gap: 4px;
        }

        .detail-item .label {
          font-weight: 600;
          color: #666;
        }

        .exclusive-badge {
          display: inline-block;
          padding: 4px 12px;
          background: #fef3c7;
          color: #92400e;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          margin-top: 8px;
        }

        .ip-objects-table {
          overflow-x: auto;
        }

        table {
          width: 100%;
          border-collapse: collapse;
        }

        th, td {
          padding: 12px;
          text-align: left;
          border-bottom: 1px solid #eee;
        }

        th {
          background: #f9fafb;
          font-weight: 600;
          color: #666;
          font-size: 14px;
        }

        td {
          font-size: 14px;
          color: #333;
        }

        td a {
          color: #2563eb;
          text-decoration: none;
          font-weight: 500;
        }

        td a:hover {
          text-decoration: underline;
        }

        .description {
          font-size: 12px;
          color: #666;
          margin-top: 4px;
        }

        tbody tr:hover {
          background: #f9fafb;
        }

        .badge {
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          display: inline-block;
        }

        .badge-draft {
          background: #f3f4f6;
          color: #6b7280;
        }

        .badge-pending_signature {
          background: #fef3c7;
          color: #92400e;
        }

        .badge-active {
          background: #dcfce7;
          color: #166534;
        }

        .badge-expired {
          background: #fee2e2;
          color: #991b1b;
        }

        .badge-terminated, .badge-cancelled {
          background: #fee2e2;
          color: #991b1b;
        }

        .badge-yes {
          background: #dcfce7;
          color: #166534;
        }

        .badge-no {
          background: #f3f4f6;
          color: #6b7280;
        }

        .empty-state {
          padding: 40px;
          text-align: center;
          color: #666;
          background: #f9fafb;
          border-radius: 6px;
        }

        .btn {
          padding: 10px 20px;
          border: 1px solid #ddd;
          border-radius: 4px;
          background: white;
          color: #333;
          text-decoration: none;
          cursor: pointer;
          font-size: 14px;
          display: inline-block;
          transition: all 0.2s;
        }

        .btn:hover {
          background: #f5f5f5;
        }

        .btn-primary {
          background: #2563eb;
          color: white;
          border-color: #2563eb;
        }

        .btn-primary:hover {
          background: #1d4ed8;
        }

        .loading, .error {
          text-align: center;
          padding: 40px;
          color: #666;
        }

        .error {
          color: #991b1b;
          background: #fee2e2;
          border-radius: 6px;
        }
      `}</style>
    </div>
  );
};

export default ContractDetail;
